import React, { createContext, useCallback, useContext, useRef, useState } from "react";

const ToastContext = createContext(null);

let idSeq = 0;

// Toasts volgens de handoff: rechtsonder, donker, 3,2 s. De stijl staat in
// styles/components.css (overschrijft de oude .toast-regels uit base.css); de animatie
// gebruikt de motion-tokens en valt weg bij prefers-reduced-motion.
export const TOAST_DURATION = 3200;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const tm = timers.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timers.current.delete(id);
    }
  }, []);

  const addToast = useCallback(({ type = "info", message, title, duration = TOAST_DURATION } = {}) => {
    if (!message) return;
    const id = ++idSeq;
    // "warning" is een gangbare alias (zoals bij Alert); de stijl kent enkel .toast--warn.
    const kind = type === "warning" ? "warn" : type;
    const toast = { id, type: kind, message, title, duration, createdAt: Date.now() };
    setToasts((prev) => [...prev, toast]);
    if (duration > 0) {
      const tm = setTimeout(() => removeToast(id), duration);
      timers.current.set(id, tm);
    }
    return id;
  }, [removeToast]);

  const api = { addToast, removeToast };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Toast UI — de stapel zelf is geen live-regio; elke toast is dat wel (status of,
          bij een fout, alert). Anders meldt de schermlezer dezelfde tekst dubbel. */}
      <div className="toast-stack" role="region" aria-label="Meldingen">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.type}`}
            role={t.type === "error" ? "alert" : "status"}
            aria-live={t.type === "error" ? "assertive" : "polite"}
          >
            <div className="toast__body">
              {t.title && <strong className="toast__title">{t.title}</strong>}
              <div className="toast__msg">{t.message}</div>
            </div>
            <button
              type="button"
              className="toast__close"
              aria-label="Melding sluiten"
              onClick={() => removeToast(t.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}

// Safe variant that returns no-op functions when provider ontbreekt
export function useOptionalToast() {
  const ctx = useContext(ToastContext);
  return ctx || { addToast: () => {}, removeToast: () => {} };
}
